import { useContext, useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';

import { AppStateContext } from '../../context/AppStateContext';
import GameEndScreen from '../GameEndScreen/GameEndScreen';

const ChatBody = ({socket, messages, typingStatus, lastMessageRef}) => {
    const {currentQuest, teamToken, teamData, gameFinished} = useContext(AppStateContext);
    const [showEndScreen, setShowEndScreen] = useState(false);

    const [disabledButtons, setDisabledButtons] = useState(false);
    const [visibleSuggestions, setVisibleSuggestions] = useState(new Set());
    const [chatInputValues, setChatInputValues] = useState({});

    const firstLoadRef = useRef(false);
    const [lastMessageActive, setLastMessageActive] = useState(false);

    const [status, setStatus] = useState('Offline');
    const statusTimeoutRef = useRef(null);

    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const [videoPopupSrc, setVideoPopupSrc] = useState(null);
    const [expandedVideoTexts, setExpandedVideoTexts] = useState(new Set());

    const toggleVideoText = (messageIndex) => {
        setExpandedVideoTexts(prev => {
            const next = new Set(prev);
            if (next.has(messageIndex)) {
                next.delete(messageIndex);
            } else {
                next.add(messageIndex);
            }
            return next;
        });
    };

    const handleSuggestionClick = (suggestion) => {
        setDisabledButtons(true);
        if(suggestion.text.trim()) {
            const nextMessage = {
                id: 'answer-ok',
                suggestionId: suggestion.id,
                name: 'you',
                text: suggestion.text,
                next_message_id: suggestion.next_message_id,
                quest_id: currentQuest.id,
                team_token: teamToken
            }
            console.log('nextMessage', nextMessage);
            socket.emit("message", nextMessage);
        }
    }

    const handleChatInputSubmit = (message, inputText) => {
        console.log('handleChatInputSubmit', message, inputText);
        if (!inputText.trim()) return;
        setDisabledButtons(true);
        const nextMessage = {
            id: 'answer-ok',
            suggestionId: `chat-input-${message.id}`,
            name: 'you',
            text: inputText.trim(),
            next_message_id: message.next_message_id,
            quest_id: currentQuest.id,
            team_token: teamToken
        };
        socket.emit("message", nextMessage);
    };

    const openPopup = () => setIsPopupOpen(true);
    const closePopup = () => setIsPopupOpen(false);

    // pełnoekranowy odtwarzacz video z krzyżykiem do zamknięcia
    const VideoPopup = () => {
        if (!videoPopupSrc) return null;

        return createPortal(
            <div className="chat__video-popup">
                <div className="chat__video-popup-overlay"></div>
                <button
                    className="chat__video-popup-close"
                    onClick={() => setVideoPopupSrc(null)}
                    aria-label="Zamknij film"
                >
                    ✖
                </button>
                <video
                    className="chat__video-popup-player"
                    src={videoPopupSrc}
                    controls
                    autoPlay
                    playsInline
                />
            </div>,
            document.body
        );
    };

        const VictorPopup = () => {
            if (!isPopupOpen) return null;
    
            return createPortal(
                <div className="chat__viktor-popup">
                    <div className="chat__viktor-popup-overlay" onClick={closePopup}></div>
                    <div className="chat__viktor-popup-container">
                        <button
                            className="chat__viktor-popup-close"
                            onClick={closePopup}
                            aria-label="Zamknij"
                        >
                            ✖
                        </button>
                        <picture className="chat__viktor-popup-picture">
                            <source srcSet="./assets/images/avatar.webp" type="image/webp" />
                            <img
                                src="./assets/images/avatar.jpg"
                                alt="Wiktor"
                                className="chat__viktor-popup-avatar"
                            />
                        </picture>
                        <div className="chat__viktor-popup-info">
                            <h2 className="chat__viktor-popup-name">Wiktor</h2>
                            <p className="chat__viktor-popup-role">Niezależny historyk</p>
                            {/*<p className="chat__viktor-popup-desc">
                                Opis
                            </p>*/}
                        </div>
                    </div>
                </div>,
                document.body
            );
        };

     useEffect(() => {
        const handler = data => {
            console.log('Block suggestions:', data);
            setDisabledButtons(true);

        };

        socket.on("blockSuggestions", handler);

    }, []);

    const markWiktorOnline = () => {
        setStatus('Online');

        if (statusTimeoutRef.current) {
            clearTimeout(statusTimeoutRef.current);
        }

        statusTimeoutRef.current = setTimeout(() => {
            setStatus('Offline');
        }, 60000);
    };

    useEffect(() => {
        if(typingStatus) {
            markWiktorOnline();
        }
    }, [typingStatus]);

    // Wiktor przechodzi w stan Online już w momencie otrzymania jego wiadomości
    // (czyli zaraz po rozwiązaniu zagadki), a nie dopiero gdy pojawia się wskaźnik pisania
    useEffect(() => {
        const handler = data => {
            if (data.name === 'Wiktor') {
                markWiktorOnline();
            }
        };

        socket.on("messageResponse", handler);

        return () => {
            socket.off("messageResponse", handler);
        };
    }, []);

    // obsługa zdarzenia od serwera – pokaż odpowiedzi o zaplanowanej godzinie
    useEffect(() => {
        const handler = ({ messageKey }) => {
            const idx = messages.findIndex(m => m.messageKey === messageKey);
            if (idx !== -1) {
                setVisibleSuggestions(prev => new Set([...prev, idx]));
            }
        };
        socket.on("userResponsesReady", handler);
        return () => socket.off("userResponsesReady", handler);
    }, [messages]);

    const refreshVisibleSuggestions = () => {
        setVisibleSuggestions(prev => {
            const next = new Set(prev);

            messages.forEach((message, index) => {
                const hasSuggestions = !!(message.user_responses || message.chat_input);
                if (!hasSuggestions) return;

                const isAlreadyVisible = next.has(index);
                if (isAlreadyVisible) return;

                if (message.user_responses_delayed && message.user_responses_ready_at) {
                    const readyAt = new Date(message.user_responses_ready_at).getTime();
                    if (readyAt <= Date.now()) {
                        next.add(index);
                    }
                    return;
                }

                next.add(index);
            });

            return next;
        });
    };

    useEffect(() => {
        setDisabledButtons(false);
        refreshVisibleSuggestions();

        // raz na wejściu zaznaczam ostatnią wiadomośc jako aktywną, a po 3 sekundach usuwam zaznaczenie
        if(messages.length && messages.length > 1 && !firstLoadRef.current) {
            firstLoadRef.current = true;
            setLastMessageActive(true);
            setTimeout(() => {
                setLastMessageActive(false);
            }, 3000);
        }

    }, [messages]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshVisibleSuggestions();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [messages]);

    // przypisanie wiadomości do zagadki – Wiktor nie ma quest_id w zapisanej historii,
    // ale jego messageKey ma format "questId:messageId". Gdy brakuje obu, wiadomość
    // dziedziczy zagadkę z poprzedniej (czat jest chronologiczny)
    const messageQuestIds = useMemo(() => {
        let lastQuestId = null;
        return messages.map(message => {
            const questId = message?.quest_id || message?.messageKey?.split(':')[0] || lastQuestId;
            lastQuestId = questId;
            return questId;
        });
    }, [messages]);

    // wiadomości z aktualnej zagadki są wyróżnione, starsze przygaszone. Jeśli w czacie
    // nie ma jeszcze wiadomości z currentQuest (np. zagadka dopiero się odblokowała),
    // wyróżniamy najnowszą zagadkę obecną w historii – inaczej przygasiłoby to cały czat
    const activeQuestId = useMemo(() => {
        if (!messageQuestIds.length) return null;
        if (currentQuest && messageQuestIds.includes(currentQuest.id)) return currentQuest.id;
        return messageQuestIds[messageQuestIds.length - 1];
    }, [messageQuestIds, currentQuest]);

    const lastOldQuestMessageIndex = useMemo(() => {
        if (!activeQuestId) return -1;

        for (let index = messageQuestIds.length - 1; index >= 0; index -= 1) {
            if (messageQuestIds[index] !== activeQuestId) {
                return index;
            }
        }

        return -1;
    }, [messageQuestIds, activeQuestId]);

    return (
    <>
        {showEndScreen && <GameEndScreen onClose={() => setShowEndScreen(false)} />}


        <header className='chat__mainHeader'>
            <picture className='chat__mainHeader-picture' onClick={openPopup}>
                <source srcSet={`./assets/images/avatar.webp`} type="image/webp" />
                <img 
                    src={'./assets/images/avatar.jpg'}
                    alt="Quest photo" 
                    className="chat__mainHeader-avatar"
                    
                    style={{ cursor: 'zoom-in' }}
                />
            </picture>
            <div className='chat__mainHeader-info'>
                <div><b>Wiktor</b></div>
                <div className={`chat__mainHeader-status ${status === 'Online' ? '-online' : '-offline'}`}>{status}</div>
            </div>
        </header>

        <VictorPopup />

        <VideoPopup />

        <div className='message__container'>
            {messages.map((message, messageIndex) => {
                const isActiveQuestMessage = !activeQuestId || messageQuestIds[messageIndex] === activeQuestId || messageIndex === lastOldQuestMessageIndex;

                return (
                <Fragment key={messageIndex}>
                    {!message.chat_input &&<div className={`message__chats ${message.name === 'you' ? '-you' : ''}`} key={message.id}>
                        <div className='message__chats-container'>
                            <div className='message__chats-header'>
                                <p className='message__chats-name'>
                                    {message.name === 'you' ? teamData.name : message.name}
                                </p>
                                <div className='message__chats-time'>
                                    {message.timestamp ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                </div>
                            </div>
                            <div className={`message__recipient ${isActiveQuestMessage ? '-current-quest' : '-past-quest'} ${lastMessageActive && messageIndex === messages.length - 1 ? '-active' : ''}`}>
                                {message.video ? (
                                    <>
                                        <div
                                            className="message__video-thumb"
                                            onClick={() => setVideoPopupSrc(message.video)}
                                            role="button"
                                            aria-label="Odtwórz film"
                                        >
                                            <video src={message.video} preload="metadata" muted playsInline />
                                            <div className="message__video-play">▶</div>
                                        </div>
                                        <button
                                            type="button"
                                            className="message__video-text-toggle"
                                            onClick={() => toggleVideoText(messageIndex)}
                                        >
                                            {expandedVideoTexts.has(messageIndex)
                                                ? 'Ukryj treść filmu ▲'
                                                : 'Pokaż treść filmu ▼'}
                                        </button>
                                        {expandedVideoTexts.has(messageIndex) && <p dangerouslySetInnerHTML={{ __html: message.text }} />}
                                    </>
                                ) : (
                                    <p dangerouslySetInnerHTML={{ __html: message.text }} />
                                )}
                                {message.img && <img src={message.img} alt="Obrazek" />}
                            </div>
                        </div>
                    </div>}
                    {message.user_responses && visibleSuggestions.has(messageIndex) && <div className='chat__footer-suggestions'>
                        <div className='chat__footer-suggestions-info'>Wybierz odpowiedź:</div>
                        {message.user_responses.map((response, userResponseIndex) => (
                            <button
                                key={userResponseIndex}
                                type="button"
                                className={`chat__footer-suggestion-btn ${messages.length-1 !== messageIndex || disabledButtons ? '-disabled' : ''}`}
                                onClick={() => handleSuggestionClick(response)}
                            >
                                {response.text}
                            </button>
                        ))}
                    </div>}
                    {message.chat_input && visibleSuggestions.has(messageIndex) && (
                        <div className='chat__footer-chat-input'>
                            <input
                                type="text"
                                className={`chat__footer-chat-input-field ${messages.length-1 !== messageIndex || disabledButtons ? '-disabled' : ''}`}
                                value={chatInputValues[messageIndex] || ''}
                                onChange={e => setChatInputValues(prev => ({...prev, [messageIndex]: e.target.value}))}
                                disabled={messages.length - 1 !== messageIndex || disabledButtons}
                                placeholder={message.chat_input.placeholder || 'Wpisz odpowiedź...'}
                                onClick={() => {
                                    const container = document.querySelector('.message__container');
                                    if (!container) return;
                                    const scrollToBottom = () => {
                                        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
                                    };
                                    if (window.visualViewport) {
                                        const onResize = () => {
                                            scrollToBottom();
                                            window.visualViewport.removeEventListener('resize', onResize);
                                        };
                                        window.visualViewport.addEventListener('resize', onResize);
                                        setTimeout(() => {
                                            window.visualViewport.removeEventListener('resize', onResize);
                                            scrollToBottom();
                                        }, 800);
                                    } else {
                                        setTimeout(scrollToBottom, 500);
                                    }
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !disabledButtons && messages.length - 1 === messageIndex) {
                                        handleChatInputSubmit(message, chatInputValues[messageIndex] || '');
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className={`chat__footer-chat-input-btn ${messages.length-1 !== messageIndex || disabledButtons ? '-disabled' : ''}`}
                                onClick={() => handleChatInputSubmit(message, chatInputValues[messageIndex] || '')}
                                disabled={messages.length - 1 !== messageIndex || disabledButtons}
                            >
                                Wyślij
                            </button>
                        </div>
                    )}
                </Fragment>
                );
            })}
            <div className='message__status'>
                <p>
                    {typingStatus && 'Wiktor pisze'}
                    {typingStatus && typingStatus.includes('...') && <span>
                        <span className="typing-dots">
                            <span>.</span>
                            <span>.</span>
                            <span>.</span>
                        </span>
                    </span>}
                </p>
            </div>
            { gameFinished && (
                <button
                    className="game-end-open-btn"
                    onClick={() => setShowEndScreen(true)}
                    style={{
                        display: 'block',
                        width: '100%',
                        color: '#fff',
                        border: '1px solid #fff',
                        borderRadius: '24px',
                        padding: '15px',
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        letterSpacing: '0.02em',
                    }}
                >
                    Przejdź do podsumowania gry
                </button>
            )}

          <div style={{height: '10px'}} ref={lastMessageRef} />   
        </div>
    </>
  )
}

export default ChatBody
